import { RelatedTag } from '@empathy/search-types';
import { container } from '../../../container';
import { DEPENDENCIES } from '../../../container/dependencies.const';
import { EmpathyXConfig, Facets, URLDefaultMappings } from '../../../models';
import { FacetsReconstructor } from '../../../services';
import { Dictionary } from '../../../utils';
import { ACTIONS_KEYS, GETTERS_KEYS, MUTATIONS_KEYS } from '../../store.const';
import { URL_ACTIONS_KEYS, URL_GETTERS_KEYS, URL_MUTATIONS_KEYS } from '../url.const';
import { URLAction, URLContext } from '../url.interfaces';

export class LoadStateFromURLAction implements URLAction {

  actions = {
    [URL_ACTIONS_KEYS.loadStateFromURL]: (context: URLContext, url: string): any => {
      context.commit(URL_MUTATIONS_KEYS.setURL, url);
      context.commit(URL_MUTATIONS_KEYS.setStateFromURL, true);
      const query = this.getQueryFromURL(context);
      if (query) {
        this.loadURLState(context);
      } else {
        this.loadQueryFromURL(context);
      }
    }
  };

  private getQueryFromURL(context: URLContext): string {
    const { searchParams, queryDefaultMappings } = this.getURLSearchParamsAndMappings(context);
    return searchParams.get(queryDefaultMappings.query);
  }

  private loadURLState(context: URLContext) {
    this.loadFirstGridItemIdInViewFromURL(context);
    this.loadQueryFromURL(context);
    this.loadPageFromURL(context);
    this.loadFiltersFromURL(context);
    this.loadRelatedTagsFromURL(context);
    this.loadSortFromURL(context);
  }

  private loadQueryFromURL(context: URLContext): string {
    const query = this.getQueryFromURL(context) ?? '';
    context.dispatch(ACTIONS_KEYS.core.setInputQuery, query, { root: true });
    context.commit(MUTATIONS_KEYS.search.setQuery, query, { root: true });
    return query;
  }

  private loadFirstGridItemIdInViewFromURL(context: URLContext): string {
    const { searchParams, queryDefaultMappings } = this.getURLSearchParamsAndMappings(context);
    const firstGridItemIdInView = searchParams.get(queryDefaultMappings.firstGridItemIdInView) ?? null;
    context.commit(MUTATIONS_KEYS.core.setFirstGridItemIdInView, firstGridItemIdInView, { root: true });
    return firstGridItemIdInView;
  }

  private loadPageFromURL(context: URLContext): number {
    const { searchParams, queryDefaultMappings } = this.getURLSearchParamsAndMappings(context);
    const page = Number(searchParams.get(queryDefaultMappings.page)) || 1;
    context.commit(MUTATIONS_KEYS.search.setPage, page, { root: true });
    return page;
  }

  private loadFiltersFromURL(context: URLContext): Facets {
    const { searchParams, queryDefaultMappings } = this.getURLSearchParamsAndMappings(context);
    const filterValues = searchParams.getAll(queryDefaultMappings.filters);
    if (filterValues.length) {
      const facetsReconstructor = container.get<FacetsReconstructor>(DEPENDENCIES.FacetsReconstructor);
      const facetValues = filterValues.reduce<Dictionary<string[]>>((parsedFacets, urlFilter) => {
        const [facetName, filterValue] = this.getFacetNameAndFilterValue(urlFilter);
        parsedFacets[facetName] = parsedFacets[facetName] ?? [];
        parsedFacets[facetName].push(filterValue);
        return parsedFacets;
      }, {});
      const facets = facetsReconstructor.reconstruct(facetValues);
      context.commit(MUTATIONS_KEYS.search.setFacets, facets, { root: true });
      return facets;
    }
    return {};
  }

  private loadRelatedTagsFromURL(context: URLContext): RelatedTag[] {
    const { searchParams, queryDefaultMappings } = this.getURLSearchParamsAndMappings(context);
    const selectedRelatedTags = searchParams.getAll(queryDefaultMappings.relatedTags);
    const query = this.getQueryFromURL(context);
    const relatedTags = selectedRelatedTags.map(tag => ({
      tag: tag,
      selected: true,
      query: `${ query } ${ tag }`,
      previous: query
    }));
    context.commit(MUTATIONS_KEYS.querySignals.setRelatedTags, relatedTags, { root: true });
    return relatedTags;
  }

  private loadSortFromURL(context: URLContext): void {
    const { searchParams, queryDefaultMappings } = this.getURLSearchParamsAndMappings(context);
    const config: EmpathyXConfig = context.rootGetters[GETTERS_KEYS.config.config];
    const urlSort = searchParams.get(queryDefaultMappings.sort);
    const selectedSort = config.sort.sorts.find(sort => sort.value === urlSort);
    if (selectedSort) {
      context.commit(MUTATIONS_KEYS.search.setSort, selectedSort, { root: true });
    }
  }

  private getFacetNameAndFilterValue(urlFilter: string): [string, string] {
    const endIndexOfFacetName = urlFilter.indexOf(':');
    return [
      urlFilter.substr(0, endIndexOfFacetName), // Facet name
      urlFilter.substr(endIndexOfFacetName + 1) // Filter value
    ];
  }

  private getURLSearchParamsAndMappings(context: URLContext):
    { searchParams: URLSearchParams, queryDefaultMappings: URLDefaultMappings } {
    return {
      searchParams: context.getters[URL_GETTERS_KEYS.url].searchParams,
      queryDefaultMappings: context.rootGetters[GETTERS_KEYS.config.url].queryDefaultMappings
    };
  }

}
